"use client";

import { useRef, useEffect, useState } from "react";
import FadeIn from "../ui/FadeIn";

const stats = [
  { value: 600, suffix: "B+", prefix: "$", label: "Client AUM" },
  { value: 10000, suffix: "+", prefix: "", label: "Companies covered" },
  { value: 99, suffix: "%", prefix: "", label: "Financial accuracy" },
  { value: 250, suffix: "M+", prefix: "", label: "Sources" },
];

function Counter({ value, suffix, prefix }: { value: number; suffix: string; prefix: string }) {
  // Starts at the final value so the server HTML (what crawlers and AI engines
  // read) says "$600B+", not "$0B+". The count-up restarts from 0 on scroll-in.
  const [displayed, setDisplayed] = useState(value);
  const [hasAnimated, setHasAnimated] = useState(false);
  const ref = useRef<HTMLSpanElement>(null);

  useEffect(() => {
    const observer = new IntersectionObserver(
      ([entry]) => {
        if (entry.isIntersecting && !hasAnimated) {
          setHasAnimated(true);
          const duration = 2000;
          const start = Date.now();
          const timer = setInterval(() => {
            const elapsed = Date.now() - start;
            const progress = Math.min(elapsed / duration, 1);
            const eased = 1 - Math.pow(1 - progress, 4);
            setDisplayed(Math.round(eased * value));
            if (progress >= 1) clearInterval(timer);
          }, 16);
          return () => clearInterval(timer);
        }
      },
      { threshold: 0.5 }
    );
    if (ref.current) observer.observe(ref.current);
    return () => observer.disconnect();
  }, [value, hasAnimated]);

  return <span ref={ref}>{prefix}{displayed.toLocaleString()}{suffix}</span>;
}

export default function Stats() {
  return (
    <section id="research" className="py-8 sm:py-12 lg:py-16 short:py-6 relative">

      <div className="max-w-[1400px] mx-auto px-5 sm:px-6 lg:px-8 relative">
        <div className="mb-10 sm:mb-14 xl:mb-16">
          <FadeIn>
            <h2 className="text-3xl md:text-4xl xl:text-5xl font-[family-name:var(--font-display)] tracking-[-0.02em]">
              Hudson Labs by the numbers
            </h2>
          </FadeIn>
        </div>

        <div className="grid grid-cols-2 xl:grid-cols-4 border-t border-black/10">
          {stats.map((stat, i) => (
            <FadeIn key={stat.label} delay={i * 0.08}>
              <div className={`py-6 sm:py-8 pr-4 ${i % 2 ? "pl-5 sm:pl-8 border-l border-black/10" : ""} ${i > 0 ? "xl:pl-8 xl:border-l xl:border-black/10" : ""} ${i < 2 ? "border-b xl:border-b-0 border-black/10" : ""}`}>
                <p className="text-4xl sm:text-5xl xl:text-6xl font-[family-name:var(--font-display)] tracking-[-0.03em] text-black tabular-nums">
                  <Counter value={stat.value} suffix={stat.suffix} prefix={stat.prefix} />
                </p>
                <p className="mt-2 text-sm text-black/55">
                  {stat.label}
                </p>
              </div>
            </FadeIn>
          ))}
        </div>
      </div>

    </section>
  );
}
